'use client';

import React, { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import Button from '@/ui/components/shared/Button';
import { apiPaths } from '@/constants/apiPaths';
import { useTranslations } from 'next-intl';
import { buildTrackingId } from '@/utils/tracking-helper';
import { ScreenToShowState } from '../../types/ScreenToShowState';

export default function EmailForm({
  setNewCustomerEmail,
  setExistingCustomerEmail,
  setScreenToShow,
  buttonText,
  onEmailSubmit,
  trackingPrefix,
}: Readonly<{
  setNewCustomerEmail: (email: string) => void;
  setExistingCustomerEmail: (email: string) => void;
  setScreenToShow: (screen: ScreenToShowState) => void;
  buttonText?: string;
  onEmailSubmit?: (email: string) => void;
  trackingPrefix?: string;
}>) {
  const t = useTranslations('ClubRegistration.EmailForm');
  const [loading, setLoading] = useState(false);
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .transform((value) => value.trim()) // Automatically trims spaces
      .email(t('Invalid email'))
      .required(t('Email is required')),
  });

  const {
    register,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<{
    email: string;
  }>({
    resolver: yupResolver(validationSchema),
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    const data = getValues();
    // remove spaces from email
    data.email = data.email.trim();

    if (onEmailSubmit) {
      onEmailSubmit(data.email);
    }

    try {
      setLoading(true);
      const response = await axios.get(
        apiPaths.customers.isCustomer + encodeURIComponent(data.email),
      );

      const { isCustomer } = response.data;
      const { isClubMember } = response.data;

      if (isCustomer) {
        if (isClubMember) {
          setScreenToShow(ScreenToShowState.WELCOME);
        } else {
          setScreenToShow(ScreenToShowState.LOGGED_IN);
          setExistingCustomerEmail(data.email);
        }
      } else {
        setNewCustomerEmail(data.email);
        setScreenToShow(ScreenToShowState.NEW_CUSTOMER);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resolvedButtonText = buttonText || t('Join the Nuki Club');
  const submitButtonTrackingId = buildTrackingId(
    trackingPrefix,
    'email-form',
    resolvedButtonText,
  );

  return (
    <form className="mx-auto mt-6 max-w-sm space-y-4" onSubmit={handleSubmit}>
      <TextInput
        id="email"
        srLabel={t('Email')}
        register={register}
        errors={errors.email}
        placeholder={t('Email')}
        className="w-full"
        makeRounded
      />
      <Button
        type="submit"
        className="w-full"
        loading={loading}
        aria-label={t('signUpForNukiClub')}
        data-tile-element-id={submitButtonTrackingId}
      >
        {resolvedButtonText}
      </Button>
    </form>
  );
}
